import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactSelect, { createFilter } from "react-select";
import { ArrowPathIcon, TagIcon } from "@heroicons/react/24/outline";
import { ExclamationTriangleIcon } from "@heroicons/react/20/solid";
import { defaultSelectStyles, selectTheme } from "../forms/Select/styles";
import { getSubtopics, getTopics } from "../../services/questionService";
import { fetchAllPages } from "../../utils/fetchAllPages";

const focusRing = "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600";
// Small text links in a line of text; on phones still a 44px target.
const linkButtonClass = `inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium text-blue-700 transition duration-200 enabled:hover:bg-blue-50 enabled:hover:text-blue-800 disabled:cursor-not-allowed disabled:opacity-40 max-md:min-h-[2.75rem] ${focusRing}`;

const trimName = (name) => (name || "").trim();

// "Topic › Subtopic" of a picked subtopic ({uuid, name, topic: {uuid, name, grade}}).
export const subtopicPath = (subtopic) =>
  [trimName(subtopic?.topic?.name), trimName(subtopic?.name)].filter(Boolean).join(" › ");

// Topics are per grade: -1 holds the teachers' topics, 0 (or none) has no grade (as in the matrix).
const gradeLabel = (t, grade) =>
  grade > 0 ? t("questions_page.filters.grade_n", { grade }) : grade === -1 ? t("questions_page.filters.teachers") : "";
// Grades in order, the teachers' and the gradeless last.
const gradeOrder = (grade) => (grade > 0 ? grade : grade === -1 ? 100 : 200);

/**
 * The topics of a subject and the subtopics of each topic, loaded once and shared by every picker of an
 * import. A subject has hundreds of topics, so subtopics come per topic, when one is chosen. A failed load
 * is forgotten, so the next attempt asks again; `peek*` give what is loaded already (no loading flash).
 */
function createCatalog(subjectUuid) {
  let topics = null;
  let topicList = null;
  const subtopics = new Map();
  const subtopicLists = new Map();
  return {
    subjectUuid,
    peekTopics: () => topicList,
    loadTopics() {
      if (!topics) {
        topics = fetchAllPages((page) => getTopics({ params: { subject: subjectUuid, ...page } })).then(
          (list) => {
            topicList = list;
            return list;
          },
          (err) => {
            topics = null;
            throw err;
          }
        );
      }
      return topics;
    },
    peekSubtopics: (topicUuid) => subtopicLists.get(topicUuid) || null,
    loadSubtopics(topicUuid) {
      if (!subtopics.has(topicUuid)) {
        subtopics.set(
          topicUuid,
          fetchAllPages((page) => getSubtopics({ params: { topic: topicUuid, ...page } })).then(
            (list) => {
              subtopicLists.set(topicUuid, list);
              return list;
            },
            (err) => {
              subtopics.delete(topicUuid);
              throw err;
            }
          )
        );
      }
      return subtopics.get(topicUuid);
    },
  };
}

// The catalog of `subjectUuid` (null without one), kept while the subject stays the same.
export function useSubtopicCatalog(subjectUuid) {
  return useMemo(() => (subjectUuid ? createCatalog(subjectUuid) : null), [subjectUuid]);
}

// A list loaded through the catalog: [list | null, failed, retry].
function useCatalogList(key, peek, load) {
  const [state, setState] = useState(() => ({ key, list: key ? peek() : null, failed: false }));
  const [attempt, setAttempt] = useState(0);
  const current = state.key === key ? state : { key, list: key ? peek() : null, failed: false };
  useEffect(() => {
    if (!key) return undefined;
    let active = true;
    const loaded = peek();
    if (loaded) {
      setState({ key, list: loaded, failed: false });
      return undefined;
    }
    setState({ key, list: null, failed: false });
    load().then(
      (list) => active && setState({ key, list, failed: false }),
      () => active && setState({ key, list: null, failed: true })
    );
    return () => {
      active = false;
    };
    // peek/load belong to `key` (the catalog and the topic make it).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, attempt]);
  return [current.list, current.failed, () => setAttempt((n) => n + 1)];
}

// The grades of the subject's topics as options ({value: "8", label: "8 класс"}), [] while they are read.
export function useGradeOptions(catalog) {
  const [t] = useTranslation();
  const [topics] = useCatalogList(
    catalog?.subjectUuid || "",
    () => catalog.peekTopics(),
    () => catalog.loadTopics()
  );
  return useMemo(() => {
    const grades = [...new Set((topics || []).map((item) => item.grade).filter((grade) => Number.isInteger(grade) && grade !== 0))];
    return grades.sort((a, b) => gradeOrder(a) - gradeOrder(b)).map((grade) => ({ value: String(grade), label: gradeLabel(t, grade) }));
  }, [topics, t]);
}

/**
 * A topic of the subject (all of them come in one list, grouped by grade and filtered here), then one of its
 * subtopics. `value` is the picked subtopic ({uuid, name, topic}) or null; onChange(subtopic | null).
 * `grade` (a number) narrows the topics to that grade's, when it has any.
 * Picking another topic clears the value, so what is shown is always what applies.
 * Menus open over the dialog (its question list scrolls) and close when that list scrolls.
 */
export function SubtopicFields({ catalog, value, onChange, grade = null, disabled = false, autoFocus = false, invalid = false, errorId }) {
  const [t] = useTranslation();
  const topicInputId = useId();
  const subtopicInputId = useId();
  const wrapperRef = useRef(null);
  // The topic chosen here while no subtopic is picked; a picked subtopic shows its own topic.
  const [pickedTopic, setPickedTopic] = useState(null);
  const topic = value?.topic || pickedTopic;
  const topicUuid = topic?.uuid || "";
  const subjectKey = catalog?.subjectUuid || "";
  const [topics, topicsFailed, retryTopics] = useCatalogList(
    subjectKey,
    () => catalog.peekTopics(),
    () => catalog.loadTopics()
  );
  const [subtopics, subtopicsFailed, retrySubtopics] = useCatalogList(
    subjectKey && topicUuid ? `${subjectKey}|${topicUuid}` : "",
    () => catalog.peekSubtopics(topicUuid),
    () => catalog.loadSubtopics(topicUuid)
  );

  // Grouped by grade: the same topic name often exists in several grades.
  const topicOptions = useMemo(() => {
    const groups = new Map();
    (topics || []).forEach((item) => {
      const itemGrade = Number.isInteger(item.grade) ? item.grade : null;
      if (!groups.has(itemGrade)) groups.set(itemGrade, []);
      const name = trimName(item.name);
      groups.get(itemGrade).push({
        value: item.uuid,
        label: name,
        topic: { uuid: item.uuid, name, grade: item.grade },
        gradeText: gradeLabel(t, itemGrade),
      });
    });
    const shown = grade !== null && groups.has(grade) ? [[grade, groups.get(grade)]] : [...groups.entries()];
    return shown
      .sort(([a], [b]) => gradeOrder(a) - gradeOrder(b))
      .map(([key, options]) => ({ label: gradeLabel(t, key) || t("folders.matrix.no_grade"), options }));
  }, [topics, grade, t]);
  const subtopicOptions = useMemo(
    () => (subtopics || []).map((item) => ({ value: item.uuid, label: trimName(item.name) })),
    [subtopics]
  );

  const topicOption = topic
    ? { value: topic.uuid, label: trimName(topic.name), topic, gradeText: gradeLabel(t, topic.grade) }
    : null;
  const subtopicOption = value ? { value: value.uuid, label: trimName(value.name) } : null;

  const pickTopic = (option) => {
    const next = option?.topic || null;
    setPickedTopic(next);
    if (value && value.topic?.uuid !== next?.uuid) onChange(null);
  };
  const pickSubtopic = (option) => onChange(option ? { uuid: option.value, name: option.label, topic } : null);

  const filterTopic = useMemo(() => createFilter({ stringify: (o) => `${o.label} ${o.data.gradeText || ""}` }), []);
  const formatTopic = (option, { context }) =>
    context === "value" && option.gradeText ? `${option.label} · ${option.gradeText}` : option.label;

  const styles = useMemo(
    () => ({
      ...defaultSelectStyles,
      menuPortal: (base) => ({ ...base, zIndex: 60 }),
      control: (base, state) => ({
        ...defaultSelectStyles.control(base, state),
        ...(invalid && !state.isFocused ? { borderColor: "#f87171", "&:hover": { borderColor: "#ef4444" } } : {}),
      }),
    }),
    [invalid]
  );
  const portal = typeof document !== "undefined" ? document.body : null;
  const selectProps = {
    styles,
    theme: selectTheme,
    menuPortalTarget: portal,
    menuPosition: "fixed",
    menuPlacement: "auto",
    // The question list scrolls: a menu left open would float away from its field.
    closeMenuOnScroll: (e) => e.target instanceof Node && e.target.contains(wrapperRef.current),
    isDisabled: disabled,
    isClearable: true,
    loadingMessage: () => t("table.loading"),
  };
  const labelClass = "mb-1 block text-xs font-medium text-slate-600";

  return (
    <div ref={wrapperRef}>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <div className="min-w-0">
          <label htmlFor={topicInputId} className={labelClass}>
            {t("folders.matrix.topic_label")}
          </label>
          <ReactSelect
            {...selectProps}
            inputId={topicInputId}
            autoFocus={autoFocus && !topic}
            options={topicOptions}
            value={topicOption}
            onChange={pickTopic}
            isLoading={!!subjectKey && !topics && !topicsFailed}
            filterOption={filterTopic}
            formatOptionLabel={formatTopic}
            placeholder={t("folders.matrix.topic_placeholder")}
            noOptionsMessage={() => t("table.no_options")}
          />
        </div>
        <div className="min-w-0">
          <label htmlFor={subtopicInputId} className={labelClass}>
            {t("my_questions.upload.subtopics.label")}
          </label>
          <ReactSelect
            {...selectProps}
            inputId={subtopicInputId}
            autoFocus={autoFocus && !!topic}
            options={subtopicOptions}
            value={subtopicOption}
            onChange={pickSubtopic}
            isDisabled={disabled || !topic}
            isLoading={!!topicUuid && !subtopics && !subtopicsFailed}
            placeholder={
              topic ? t("my_questions.upload.subtopics.placeholder") : t("my_questions.upload.subtopics.pick_topic_first")
            }
            noOptionsMessage={({ inputValue }) =>
              subtopics && !subtopics.length && !inputValue
                ? t("my_questions.upload.subtopics.no_subtopics")
                : t("table.no_options")
            }
            aria-invalid={invalid || undefined}
            aria-errormessage={invalid ? errorId : undefined}
          />
        </div>
      </div>
      {(topicsFailed || subtopicsFailed) && (
        <p role="alert" className="mt-1.5 flex flex-wrap items-center gap-2 text-xs font-medium text-red-700">
          {topicsFailed ? t("folders.matrix.topics_failed") : t("folders.matrix.subtopics_failed")}
          <button type="button" onClick={topicsFailed ? retryTopics : retrySubtopics} className={linkButtonClass}>
            <ArrowPathIcon className="h-3.5 w-3.5" aria-hidden="true" />
            {t("folders.matrix.retry")}
          </button>
        </p>
      )}
    </div>
  );
}

// The box of a question's topic and subtopic, by what it has: none (a warning when required), the default, its own.
const SUBTOPIC_BOX = {
  missing: "bg-amber-50 ring-amber-300",
  none: "bg-slate-50 ring-slate-200",
  general: "bg-slate-50 ring-slate-200",
  own: "bg-blue-50/60 ring-blue-200",
};
const boxButtonClass = `inline-flex min-h-[2rem] items-center rounded-md bg-white px-2.5 text-sm font-medium text-blue-700 shadow-sm ring-1 ring-inset ring-slate-300 transition duration-200 enabled:hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-40 max-md:min-h-[2.75rem] ${focusRing}`;

/**
 * The topic and subtopic of one previewed question, in a box of its own so that it is seen: its own (`own`), else
 * the import's default (`general`), else none (amber when the subject requires one). "Choose the topic and
 * subtopic" / "Change" opens the fields in the box; picking a subtopic closes them. onChange(index, subtopic |
 * null), null = back to the default. `grade` narrows the topics. `invalid`: the server refused the question's own
 * subtopic (400 invalid_subtopics).
 */
export function QuestionSubtopic({ index, catalog, own, general, grade = null, required = false, invalid = false, disabled = false, onChange }) {
  const [t] = useTranslation();
  const panelId = useId();
  const errorId = useId();
  const toggleRef = useRef(null);
  const [open, setOpen] = useState(false);
  const wasOpen = useRef(open);

  // Back to the toggle when the fields close (they held the focus), so keyboard focus is not lost.
  useEffect(() => {
    if (wasOpen.current && !open) toggleRef.current?.focus({ preventScroll: true });
    wasOpen.current = open;
  }, [open]);

  const pick = (subtopic) => {
    onChange(index, subtopic);
    if (subtopic) setOpen(false);
  };

  const state = own ? "own" : general ? "general" : required ? "missing" : "none";
  let shown;
  if (own) {
    shown = <span className="min-w-0 break-words font-medium text-slate-900">{subtopicPath(own)}</span>;
  } else if (general) {
    shown = (
      <span className="min-w-0 break-words text-slate-700">
        {subtopicPath(general)}
        <span className="ml-1.5 rounded bg-white px-1.5 py-0.5 text-[11px] font-medium text-slate-600 ring-1 ring-inset ring-slate-200">
          {t("my_questions.upload.subtopics.general_tag")}
        </span>
      </span>
    );
  } else {
    shown = (
      <span className={`inline-flex items-center gap-1 ${required ? "font-semibold text-amber-900" : "text-slate-500"}`}>
        {required && <ExclamationTriangleIcon className="h-4 w-4 shrink-0 text-amber-500" aria-hidden="true" />}
        {t("my_questions.upload.subtopics.not_chosen")}
      </span>
    );
  }

  // The buttons' names start with what they show, then say whose subtopic it is (one line per question).
  const named = (text) => `${text} (${t("my_questions.upload.subtopics.of_question", { index })})`;
  const toggleText = open
    ? t("buttons.close")
    : own
      ? t("my_questions.upload.subtopics.change")
      : general
        ? t("my_questions.upload.subtopics.choose_own")
        : t("my_questions.upload.subtopics.choose");
  const resetText = general ? t("my_questions.upload.subtopics.use_general") : t("my_questions.upload.subtopics.clear");

  return (
    <div className={`mt-2.5 rounded-lg px-3 py-2 ring-1 ring-inset ${SUBTOPIC_BOX[state]}`}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-sm">
        <TagIcon className={`h-4 w-4 shrink-0 ${state === "missing" ? "text-amber-600" : state === "own" ? "text-blue-600" : "text-slate-400"}`} aria-hidden="true" />
        <span className="font-medium text-slate-600">{t("my_questions.upload.subtopics.question_label")}:</span>
        {shown}
        <span className="ml-auto flex flex-wrap items-center gap-1.5">
          <button
            ref={toggleRef}
            type="button"
            onClick={() => setOpen((value) => !value)}
            disabled={disabled}
            aria-expanded={open}
            aria-controls={open ? panelId : undefined}
            aria-label={named(toggleText)}
            className={boxButtonClass}
          >
            {toggleText}
          </button>
          {own && !open && (
            <button
              type="button"
              onClick={() => {
                onChange(index, null);
                // This button goes with the own subtopic: the toggle next to it keeps the focus.
                toggleRef.current?.focus({ preventScroll: true });
              }}
              disabled={disabled}
              aria-label={named(resetText)}
              className={linkButtonClass}
            >
              {resetText}
            </button>
          )}
        </span>
      </div>
      {invalid && (
        <p id={errorId} className="mt-1 flex items-start gap-1.5 text-xs font-medium text-red-700">
          <ExclamationTriangleIcon className="mt-px h-3.5 w-3.5 shrink-0 text-red-500" aria-hidden="true" />
          {t("my_questions.upload.subtopics.invalid")}
        </p>
      )}
      {open && (
        <div id={panelId} className="mt-2 rounded-lg border border-slate-200 bg-white p-3">
          <SubtopicFields
            catalog={catalog}
            value={own}
            onChange={pick}
            grade={grade}
            disabled={disabled}
            autoFocus
            invalid={invalid}
            errorId={invalid ? errorId : undefined}
          />
        </div>
      )}
    </div>
  );
}
